import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpenText, CalendarCheck, Check, Crown, Headphones, Smartphone, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/cast/Common";
import { RestoreHelp } from "@/components/cast/RestoreHelp";
import { SharePlus } from "@/components/cast/SharePlus";

const PERKS = [
  { icon: BookOpenText, t: "Deep Dive Bible", d: "The whole Bible with colour highlights, page colours, day and night reading and a study guide that cites Scripture.", to: "/deep-dive" },
  { icon: Headphones, t: "Narrated chapters", d: "Any chapter read aloud by Cast's narrator voices.", to: "/deep-dive" },
  { icon: CalendarCheck, t: "Daily reading plans", d: "Psalms in 30 days, the Life of Jesus and more, with gentle evening reminders.", to: "/plans" },
  { icon: Users, t: "The whole family", d: "One membership covers you and your children's Kids Cove profiles.", to: "/parent" },
];

export default function CastPlus() {
  const { user } = useAuth();
  const on = !!user?.is_plus;
  const guest = on && !user?.plus && user?.role !== "admin" && !(user?.plus_until && new Date(user.plus_until) > new Date());
  return (
    <div data-testid="plus-page">
      <PageHeader metaphor="Cast Plus" label="Membership" title="Go deeper, together." subtitle="Cast Plus unlocks the immersive Deep Dive Bible, narrated chapters and daily reading plans for your whole family." />
      <div className={`mb-10 flex flex-wrap items-center gap-4 rounded-3xl p-6 ${on ? "bg-emerald-50 text-emerald-900" : "bg-[#1E293B] text-amber-50"}`} data-testid="plus-status">
        <Crown className={`h-8 w-8 ${on ? "text-emerald-600" : "text-amber-300"}`} />
        <div className="min-w-0 flex-1">
          <p className="font-display text-2xl font-semibold">{guest ? "You're enjoying a Cast Plus guest pass" : on ? "You're a Cast Plus member" : "Cast Plus is coming with the Cast app"}</p>
          <p className={`text-sm ${on ? "text-emerald-800" : "text-amber-100/80"}`}>{guest ? "Everything below is unlocked for 30 days. To keep it after that, subscribe in the Cast app with this same account." : on ? "Everything below is unlocked for you and your family. Thank you for supporting Cast." : "Subscribe in the Cast app for iPhone or Android, then sign in here with the same email. Your membership unlocks on the web too."}</p>
          {user?.plus_guest_until && new Date(user.plus_guest_until) > new Date() && !user?.plus && !(user?.plus_until && new Date(user.plus_until) > new Date()) && user?.role !== "admin" && (
            <p className="mt-1 text-sm font-semibold text-emerald-900" data-testid="plus-guest">Guest pass from {user.plus_guest_from} · until {new Date(user.plus_guest_until).toLocaleDateString()}</p>
          )}
          {on && user?.plus_until && !user?.plus && user?.role !== "admin" && (
            <p className="mt-1 text-sm font-semibold text-emerald-900" data-testid="plus-renewal">
              {user.plus_store?.store === "PLAY_STORE" ? "Google Play" : user.plus_store?.store === "APP_STORE" ? "App Store" : "App"} membership · {user.plus_store?.will_renew === false ? "ends" : "renews"} {new Date(user.plus_until).toLocaleDateString()}
            </p>
          )}
        </div>
        {on && <Link to="/deep-dive" data-testid="plus-open-deep" className="rounded-full bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white">Open Deep Dive</Link>}
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        {PERKS.map((p, i) => (
          <motion.div key={p.t} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
            <Link to={p.to} data-testid={`plus-perk-${i}`} className="card-warm flex h-full gap-4 p-6 transition-transform hover:-translate-y-0.5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-800"><p.icon className="h-5 w-5" /></span>
              <span><span className="block font-display text-2xl font-semibold text-slate-900">{p.t}</span><span className="mt-1 block text-sm text-slate-600">{p.d}</span></span>
            </Link>
          </motion.div>
        ))}
      </div>
      <div className="mt-10 grid gap-5 sm:grid-cols-2" data-testid="plus-prices">
        {[["Monthly", "$4.99", "a month"], ["Yearly", "$39.99", "a year · save 33%"]].map(([n, p, s]) => (
          <div key={n} className="card-warm p-6">
            <p className="eyebrow">{n}</p>
            <p className="mt-2 font-display text-5xl font-semibold text-slate-900">{p}</p>
            <p className="text-sm text-slate-500">{s}</p>
            <p className="mt-4 flex items-center gap-2 text-sm text-slate-600"><Check className="h-4 w-4 text-emerald-600" />Covers the whole family</p>
          </div>
        ))}
      </div>
      <p className="mt-6 flex items-center gap-2 text-sm text-slate-500"><Smartphone className="h-4 w-4" />Memberships are bought and managed in the Cast app through the App Store or Google Play.</p>
      {!on && <RestoreHelp />}
      <SharePlus />
    </div>
  );
}
